import { CommonModule, formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, ViewChild } from '@angular/core';
import dayjs from 'dayjs';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { DownloadTemplateButtonComponent } from '@features/download-template-button/download-template-button.component';

import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { File } from '@shared/components/file-manager/state/file.model';
import { IconComponent } from '@shared/components/icon/icon.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ApiService, FileMetadata } from '@shared/services/api.service';
import { EntityType, EventType, TemplateType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { TrialsQuery } from '@shared/store/trials/trials.query';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, DownloadTemplateButtonComponent, FileManagerComponent, IconComponent],
  selector: 'aux-bulk-upload-po-dialog',
  templateUrl: './bulk-upload-po-dialog.component.html',
})
export class BulkPoDialogComponent {
  public ref = inject(CustomOverlayRef<unknown, { type: 'UPDATE' | 'UPLOAD' }>);

  private apiService = inject(ApiService);
  private eventService = inject(EventService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private trialsQuery = inject(TrialsQuery);

  @ViewChild(FileManagerComponent) fileManager: FileManagerComponent | undefined;
  isCurrentTrialCMC: boolean = false;
  loading$ = new BehaviorSubject(false);
  metadata: FileMetadata = {};
  type: 'UPDATE' | 'UPLOAD' = 'UPLOAD';
  updateAllTrialsTemplateType = TemplateType.BULK_UPDATE_ALL_TRIALS_PURCHASE_ORDER_TEMPLATE;
  updateTemplateType = TemplateType.BULK_UPDATE_PURCHASE_ORDER_TEMPLATE;
  uploadTemplateType = TemplateType.BULK_PURCHASE_ORDER_TEMPLATE;

  constructor() {
    if (this.ref.data && this.ref.data.type) this.type = this.ref.data.type;

    this.isCurrentTrialCMC = this.trialsQuery.isCurrentTrialCMC(
      this.mainQuery.mainState().trialKey
    )();
  }

  getFilePath() {
    const formatted_current_month = formatDate(dayjs().toString(), 'MMMM-y', 'en-US');
    const trialId = this.mainQuery.getValue().trialKey;

    return `trials/${trialId}/${this.type === 'UPLOAD' ? 'po-bulk-upload' : 'po-bulk-update'}/${formatted_current_month}/`;
  }

  isSaveButtonDisabled(): boolean {
    return (
      !!this.fileManager?.fileQuery.getAll() &&
      (this.fileManager?.fileQuery.getAll().length > 1 ||
        this.fileManager?.fileQuery.getAll().length === 0)
    );
  }

  async onSave() {
    if (this.fileManager && !this.loading$.getValue()) {
      const file = this.fileManager.fileQuery.getAll()[0];

      this.loading$.next(true);

      const filePath = this.getFilePath();

      this.fileManager.fileStore.update(file.id, {
        ...file,
        bucket_key: `${filePath}${file.bucket_key}`,
      });

      const metadata: FileMetadata = { admin: '1' };
      const fileSuccess = await this.fileManager.fileService.uploadFiles({ metadata: metadata });

      if (fileSuccess) {
        const { errors, success } = await firstValueFrom(
          this.eventService.processEvent$({
            bucket_key: `public/${filePath}${file.bucket_key}`,
            entity_id: '',
            entity_type: EntityType.TRIAL,
            type:
              this.type === 'UPLOAD'
                ? EventType.PURCHASE_ORDER_BULK_TEMPLATE_UPLOADED
                : EventType.PURCHASE_ORDER_BULK_UPDATE_TEMPLATE_UPLOADED,
          })
        );

        if (success) {
          this.overlayService.success();
        } else {
          const removeFiles = async () => {
            await this.apiService.removeFile(`${filePath}${file.bucket_key}`);
          };

          removeFiles().catch((error: Error) => {
            console.error('Error removing files:', error);
          });

          this.overlayService.error(errors, undefined, true);
        }

        this.ref.close(true);
      }
    }

    this.loading$.next(false);
  }

  pathFn: () => string = () => '';

  removeFile(file: File) {
    if (this.fileManager) {
      void this.fileManager.removeFile(file);
    }
  }

  saveButtonTooltip(): string {
    if (this.fileManager?.fileQuery.getAll()) {
      if (this.fileManager?.fileQuery.getAll().length > 1) {
        return MessagesConstants.FILE.MAX_ONE_TEMPLATE;
      }

      if (this.fileManager?.fileQuery.getAll().length === 0) {
        return MessagesConstants.FILE.NEED_UPLOAD_FILE;
      }
    }

    return '';
  }
}
